<template>
	<Modal class="scoreDetailsForSKUModal" fullscreen>
		<template #title>
			<div class="font-h3-bold flex items-center gap-03 text-normal">
				<span class="text-ink-87">{{ data.humanName }}</span>
				{{ t('saleDaysSummaryPanel.skuDetails.eventsComparison') }}
			</div>
		</template>

		<div class="scoreDetailsForSKUModalContent mt-06 flex flex-col">
			<div class="top-part flex w-full items-end justify-between">
				<div class="grid w-fit grid-cols-[auto_auto_auto_auto_auto_auto]">
					<div class="captionCell pl-0">{{ t('saleDaysSummaryPanel.store') }}</div>
					<div class="captionCell">{{ t('saleDaysSummaryPanel.datesRange') }}</div>
					<div class="captionCell">{{ t('saleDaysSummaryPanel.available') }}</div>
					<div class="captionCell">{{ t('saleDaysSummaryPanel.onSale') }}</div>
					<div class="captionCell">{{ t('saleDaysSummaryPanel.missed') }}</div>
					<div class="captionCell">{{ t('saleDaysSummaryPanel.score') }}</div>

					<div class="contentCell flex items-center overflow-x-hidden rounded-sm pl-0 text-ink-87">
						<PortalIcon :portal="data.portal" class="icon--size-1_5 mr-03" />

						<span class="uppercase">
							{{ data.storeName }}
						</span>
					</div>

					<div class="contentCell text-ink-87">
						{{ periodDescription }}
					</div>

					<div class="contentCell text-ink-87">{{ data.availableSaleDays }}</div>
					<div class="contentCell text-ink-87">{{ data.actualSaleDays }}</div>
					<div :class="`contentCell text-score-${getScoreColor(data.score)}`">
						{{ data.missedSaleDays }}
					</div>
					<div :class="`contentCell text-score-${getScoreColor(data.score)}`">{{ formatScore(data.score, 0) }}%</div>
				</div>

				<ButtonGroupToggle
					v-model="timelineResolutionPreset"
					:values="timelineResolutionPresets"
					size="s"
					class="resolutionPresetButtons ml-09"
				/>
			</div>

			<div class="mt-06 flex w-full flex-col">
				<div class="timelineWrapper max-w-[85vw] rounded bg-level-00 px-05 py-03 pl-03">
					<EventsTimeline :products="products" :events="events" :resolution="resolution" readonly use-flat-resources />
				</div>

				<div class="mt-06 flex w-full justify-end">
					<Button size="s" variant="primary" @click="emit('close')">{{ t('saleDaysSummaryPanel.skuDetails.close') }}</Button>
				</div>
			</div>
		</div>
	</Modal>
</template>

<script setup lang="ts">
import { Button, ButtonGroupToggle, Modal, PortalIcon, useTranslation } from '@bluebrick/saas-ui'
import { type MbscResource } from '@mobiscroll/vue'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, toRefs } from 'vue'
import { eventDataToTimelineEvent, formatScore, getScoreColor } from '@/common/utils'
import EventsTimeline from '@/features/events/components/timeline/EventsTimeline.vue'
import { type Preferences } from '@/features/preferences/types'
import { usePreferencesStore } from '@/features/preferences/usePreferencesStore'
import { useScorePeriods } from '@/features/saleDaysSummary/composables/useScorePeriods'
import { type SaleDaysDetailsForSKU } from '@/features/saleDaysSummary/types'
import { useStoresStore } from '@/features/stores/useStoresStore'
import { type EventDataWithStore, type TimelineEvent } from '@/types'

const { t } = useTranslation()
const { isUsingStoreSpecificTimeZones } = storeToRefs(usePreferencesStore())
const { storeIdToStore } = storeToRefs(useStoresStore())

const props = defineProps<{
	data: SaleDaysDetailsForSKU
}>()

const emit = defineEmits(['close'])

const { data } = toRefs(props)

const { periods } = useScorePeriods()

const products = ref<MbscResource[]>([])
const events = ref<TimelineEvent[]>([])

const timelineResolutionPresets = ref([
	{ label: t('timelineResolution.days'), value: 'daily' as Preferences['timelineResolutionPreset'] },
	{ label: t('timelineResolution.months'), value: 'monthly' as Preferences['timelineResolutionPreset'] }
])
const timelineResolutionPreset = ref<'daily' | 'monthly'>('daily')

const resolution = computed(() => {
	switch (timelineResolutionPreset.value) {
		case 'daily':
			return 'day'

		case 'monthly':
			return 'month'

		default:
			return 'day'
	}
})

const periodDescription = computed(() => {
	return periods.value.items?.find((period) => period.id === data.value.period)?.name ?? ''
})

const store = computed(() => {
	if (!data.value.recommendedTimeline || data.value.recommendedTimeline.length === 0) {
		return
	}

	return storeIdToStore.value[data.value.recommendedTimeline[0].store_name]
})

onMounted(() => {
	const timeZone = isUsingStoreSpecificTimeZones.value ? (store.value?.timeZone ?? 'local') : 'local'

	products.value.push({
		id: data.value.uniqueSkuId,
		name: t('saleDaysSummaryPanel.skuDetails.actualTimeline'),
		cssClass: 'actualTimelineResource'
	})

	const hiddenBoundaryEvents: EventDataWithStore[] = []

	if (data.value.recommendedTimeline && data.value.recommendedTimeline.length > 0) {
		// Wrapping recommended timeline with a parent which is rendered with as an empty row that
		// will separate actual and advised timeline rows
		products.value.push({
			id: `${data.value.recommendedTimeline[0].unique_sku_id} - parent`,
			name: '',
			children: [
				{
					id: data.value.recommendedTimeline[0].unique_sku_id,
					name: t('saleDaysSummaryPanel.skuDetails.recommendedTimeline'),
					cssClass: 'recommendedTimelineResource'
				}
			]
		})

		// Add a hidden boundary event which will make sure that the timeline will show entire active
		// period. Without it, if the period is March 1st - May 15th and the only events
		// (recommended and actual) span between March 7th and April 22nd then the timeline would just
		// show the days between March 7th and April 22nd instead of entire span of March 1st - May 15th
		hiddenBoundaryEvents.push({
			...data.value.recommendedTimeline[0],
			start_date: data.value.startDate.minus({ second: 1 }),
			end_date: data.value.endDate.plus({ second: 1 }),
			hidden: true,
			name: 'hidden-boundary-event'
		})
	}

	events.value = [...data.value.recommendedTimeline, ...data.value.actualTimeline, ...hiddenBoundaryEvents].map((event) =>
		eventDataToTimelineEvent(event, event.unique_sku_id, data.value.storeId, timeZone)
	)
})
</script>

<style lang="scss">
/*
Importing `saas-ui.css` is necessary for @apply to work with font names.
But it also messes up default style specificity a bit (margins and paddings become `0` even
if using `p-*` or `m-*` classes).
Therefore we leave this stylesheet as NOT scoped which solves the problem
 */
@import '@bluebrick/saas-ui/saas-ui.css';

.scoreDetailsForSKUModalContent {
	.captionCell {
		@apply font-caption whitespace-nowrap px-05 py-03 text-ink-60;
	}

	.contentCell {
		@apply font-body-m-bold whitespace-nowrap bg-level-01 px-05 py-02;
	}

	.timelineWrapper {
		$timelineBackgroundColor: theme('colors.level.00');

		background-color: $timelineBackgroundColor;

		.scheduler .mbsc-timeline-resource-col {
			background-color: $timelineBackgroundColor;
		}

		.scheduler .mbsc-timeline-resource-bg {
			background-color: $timelineBackgroundColor;
		}

		.scheduler .mbsc-timeline-parent.mbsc-timeline-row {
			background-color: $timelineBackgroundColor;
		}

		.scheduler .mbsc-timeline-header-bg {
			background-color: $timelineBackgroundColor;
		}

		.scheduler .mbsc-timeline-header-month-text {
			background-color: $timelineBackgroundColor;
		}

		.scheduler .mbsc-timeline-resource-col {
			width: 6rem;
		}

		::-webkit-scrollbar-thumb:horizontal {
			border-top-color: $timelineBackgroundColor;
		}

		::-webkit-scrollbar-thumb:vertical {
			border-left-color: $timelineBackgroundColor;
		}

		.scheduler .actualTimelineResource .resourceName {
			color: theme('colors.ink.87') !important;
		}

		.scheduler .recommendedTimelineResource .resourceName {
			color: theme('colors.message.success') !important;
		}

		.scheduler .timelineEvent .dimmer {
			/* Do not dim events in the past */
			opacity: 0;
		}
	}
}
</style>
